/**
 * Event Attendance Component
 * Displays event attendance metrics in a card layout
 */

import {
  Box,
  Paper,
  Card,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material'
import { Event, InfoOutlined } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { EventAttendance } from '../../types'

interface EventAttendanceProps {
  data: EventAttendance
}

export default function EventAttendanceComponent({
  data,
}: EventAttendanceProps) {
  let theme = useTheme()

  let metrics = [
    {
      label: 'Total Attendances',
      value: data.totalAttendances.toString(),
      tooltip: 'Event attendance records',
      color: theme.palette.analyticsColors.activities, // Using activities color for attendance count
    },
    {
      label: 'Unique Attendees',
      value: data.uniqueAttendees.toString(),
      tooltip: 'Distinct users who attended',
      color: theme.palette.analyticsColors.userMetrics, // Using userMetrics color for user count
    },
    {
      label: 'Events with Attendance',
      value: data.eventsWithAttendance.toString(),
      tooltip: 'Events that had attendance',
      color: theme.palette.analyticsColors.activities, // Using activities color for events count
    },
  ]

  return (
    <Paper sx={{ p: 3 }}>
      <Typography
        variant="h6"
        sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Event />
        Event Attendance
      </Typography>

      <Box className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {metrics.map((metric) => (
          <Box key={metric.label}>
            <Card
              variant="outlined"
              sx={{ p: 2, textAlign: 'center', height: '100%' }}
            >
              <Typography variant="h3" sx={{ mb: 1, color: metric.color }}>
                {metric.value}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {metric.label}
                </Typography>
                <Tooltip title={metric.tooltip} arrow>
                  <IconButton size="small" sx={{ p: 0.25 }}>
                    <InfoOutlined
                      sx={{ fontSize: 16, color: 'text.disabled' }}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </Card>
          </Box>
        ))}
      </Box>
    </Paper>
  )
}
